import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, TemplateRef} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {NgbDateStruct, NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';
import * as moment from 'moment';
import {UserService} from 'app/services/user.service';
import {EmployeeProbationResponse, ProbationTraining} from './employee-probation.models';

const EMPTY = '';
const INITIAL_VERSION = 0;
const CALENDAR_DATE = 'YYYY-MM-DD';
const MINSK_OFFSET = 180;
const NEXT_DAY = 1;
const DAY = 'day';
const MONTH_OFFSET = 1;
const CONFLICT = 409;
const FORBIDDEN = 403;
const BAD_REQUEST = 400;
const SERVER_ERROR = 500;
const OFFLINE = 0;
const GRANDFATHER = 'GRANDFATHER';
const PROFILE_ROUTE = '/dashboard/profile/';
const MESSAGE = {
  LOAD_ERROR: 'PROBATION_LOAD_ERROR', DATE_FUTURE: 'PROBATION_DATE_FUTURE', STALE: 'PROBATION_STALE',
  PERMISSION: 'PROBATION_PERMISSION_REQUIRED', CHECKING: 'PROBATION_CHECKING_RESULT',
  RETRY: 'PROBATION_RETRY_ACTION', UPDATE_ERROR: 'PROBATION_UPDATE_ERROR'
};

@Component({
  selector: 'app-employee-probation',
  templateUrl: './employee-probation.component.html',
  styleUrls: ['./employee-probation.component.css']
})
export class EmployeeProbationComponent implements OnChanges, OnDestroy {
  @Input() userId: string;
  @Input() initialState: EmployeeProbationResponse;
  @Input() compact = false;
  @Input() readOnly = false;
  @Input() employeeName: string;
  @Input() departmentName: string;
  @Output() changed = new EventEmitter<EmployeeProbationResponse>();
  status: EmployeeProbationResponse = null;
  busy = false;
  loading = false;
  readDenied = false;
  messageKey = EMPTY;
  date: NgbDateStruct;
  minimumDate: NgbDateStruct;
  private request: Subscription;
  private modal: NgbModalRef;
  private uncertainDate: string = null;
  private checkingResult = false;
  private permissionDenied = false;

  constructor(private users: UserService, private modals: NgbModal) {}

  ngOnChanges(): void {
    this.cancelRequest(); this.closeModal();
    this.status = null; this.busy = false; this.loading = false;
    this.date = null; this.messageKey = EMPTY; this.checkingResult = false;
    this.permissionDenied = false;
    this.readDenied = false;
    if (!this.userId) { return; }
    if (this.initialState && this.initialState.userId === this.userId) { this.status = this.initialState; return; }
    if (this.compact) { this.messageKey = MESSAGE.LOAD_ERROR; return; }
    this.refresh();
  }

  ngOnDestroy(): void { this.cancelRequest(); this.closeModal(); }

  refresh(): void {
    this.cancelRequest(); this.loading = true;
    const target = this.userId;
    this.request = this.users.getEmployeeProbation(target).subscribe(
      response => this.acceptRead(target, response.body),
      error => {
        this.loading = false; this.busy = false;
        this.readDenied = error.status === FORBIDDEN;
        this.messageKey = this.readDenied ? EMPTY : MESSAGE.LOAD_ERROR;
        if (this.readDenied) { this.closeModal(); }
      }
    );
  }

  approve(): void {
    if (!this.status || !this.status.canApprove || !this.canAct) { return; }
    this.uncertainDate = null;
    this.submit(this.users.approveEmployeeProbation(this.userId));
  }

  openSnooze(content: TemplateRef<Object>): void {
    if (!this.status || !this.status.canSnooze || !this.canAct) { return; }
    const tomorrow = moment.utc().utcOffset(MINSK_OFFSET).add(NEXT_DAY, DAY);
    this.minimumDate = {year: tomorrow.year(), month: tomorrow.month() + MONTH_OFFSET, day: tomorrow.date()};
    this.modal = this.modals.open(content, {beforeDismiss: () => !this.busy});
    this.modal.result.then(() => this.modal = null, () => this.modal = null);
  }

  snooze(): void {
    if (!this.status || !this.status.canSnooze || !this.canAct) { return; }
    const date = this.calendarDate();
    if (!date || date <= moment.utc().utcOffset(MINSK_OFFSET).format(CALENDAR_DATE)) {
      this.messageKey = MESSAGE.DATE_FUTURE; return;
    }
    this.uncertainDate = date;
    this.submit(this.users.snoozeEmployeeProbation(this.userId,
      {date: date, expectedVersion: this.status.version == null ? INITIAL_VERSION : this.status.version}));
  }

  get canAct(): boolean { return !this.readOnly && !this.readDenied && !this.busy && !this.loading && !this.checkingResult && !this.permissionDenied; }
  get profileLink(): string { return PROFILE_ROUTE + encodeURIComponent(this.userId); }
  get grandfathered(): boolean { return !!this.status && this.status.approvalSource === GRANDFATHER; }
  get missingTrainings(): ProbationTraining[] {
    return (this.status && this.status.trainings || []).filter(training => training.currentLevel < training.requiredLevel);
  }
  trackTraining(index: number, training: ProbationTraining): string { return training.specializationId; }
  trackBlocker(index: number, blocker: string): string { return blocker; }

  private submit(operation: Observable<HttpResponse<EmployeeProbationResponse>>): void {
    this.busy = true; this.messageKey = EMPTY;
    this.request = operation.subscribe(response => this.complete(response.body), error => this.failed(error));
  }

  private complete(status: EmployeeProbationResponse): void {
    this.busy = false;
    if (!status || status.userId !== this.userId) { this.messageKey = MESSAGE.LOAD_ERROR; return; }
    this.status = status; this.checkingResult = false;
    this.closeModal(); this.changed.emit(status);
  }

  private acceptRead(target: string, status: EmployeeProbationResponse): void {
    if (target !== this.userId) { return; }
    this.loading = false; this.busy = false;
    if (!status || status.userId !== target) { this.messageKey = MESSAGE.LOAD_ERROR; return; }
    this.status = status;
    this.permissionDenied = false;
    if (this.checkingResult) {
      if (status.approved || this.uncertainDate && status.snoozedUntil === this.uncertainDate) {
        this.messageKey = EMPTY; this.complete(status); return;
      }
      this.messageKey = MESSAGE.RETRY;
    }
    this.checkingResult = false;
  }

  private failed(error: HttpErrorResponse): void {
    this.busy = false;
    if (error.status === CONFLICT) { this.messageKey = MESSAGE.STALE; this.refresh(); return; }
    if (error.status === FORBIDDEN) { this.permissionDenied = true; this.messageKey = MESSAGE.PERMISSION; return; }
    if (error.status === BAD_REQUEST) { this.messageKey = MESSAGE.DATE_FUTURE; return; }
    if (!error.status || error.status === OFFLINE || error.status >= SERVER_ERROR) {
      this.messageKey = MESSAGE.CHECKING; this.checkingResult = true; this.refresh(); return;
    }
    this.messageKey = MESSAGE.UPDATE_ERROR;
  }

  private calendarDate(): string {
    if (!this.date) { return null; }
    const parsed = moment({year: this.date.year, month: this.date.month - MONTH_OFFSET, day: this.date.day});
    return parsed.isValid() ? parsed.format(CALENDAR_DATE) : null;
  }

  private cancelRequest(): void { if (this.request) { this.request.unsubscribe(); } }
  private closeModal(): void { if (this.modal) { this.modal.close(); this.modal = null; } }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/employee-probation/employee-probation.component.ts